import type { ChatSummary, MessageDto, Page } from '../api/types';

const TECHNICAL_DIAGNOSTIC_PREFIXES = ['📋 KMP-DEBUG', '🐛 CRASH'] as const;

/** Внутренние сообщения DebugReporter в чате поддержки не входят в обычную ленту. */
export function isTechnicalDiagnosticMessage(
  message: MessageDto,
  isSupportChat: boolean,
  isSupportOperator = false,
): boolean {
  if (!isSupportChat || isSupportOperator || message.type !== 1) return false;
  if (message.payload.technicalDiagnostic === true) return true;
  const text = message.payload.data?.text?.trimStart();
  return !!text && TECHNICAL_DIAGNOSTIC_PREFIXES.some((prefix) => text.startsWith(prefix));
}

/** Чат без подтверждённого типа временно считаем «Поддержкой»: не допускаем утечку при восстановлении ленты. */
export function hideTechnicalDiagnostics(
  messages: MessageDto[],
  isSupportChat: boolean | null,
  isSupportOperator: boolean,
): MessageDto[] {
  return messages.filter((message) => !isTechnicalDiagnosticMessage(message, isSupportChat ?? true, isSupportOperator));
}

export function canLoadOlderMessages(messages: MessageDto[] | undefined, beforeCursor: number | null | undefined): boolean {
  return !!messages && (messages.length > 0 || beforeCursor != null);
}

export const SUPPORT_DIAGNOSTIC_SCAN_MAX_PAGES = 20;

type SupportHistoryLoader = (chatId: string, before: number | undefined, limit: number) => Promise<Page<MessageDto>>;

/** Fail-closed for unknown identity: hide Support diagnostics until role and unread owner are known. */
export async function hideTechnicalSupportMessages(
  chats: ChatSummary[],
  currentUserId: string | null,
  supportOperator: boolean,
  loadHistory: SupportHistoryLoader,
): Promise<ChatSummary[]> {
  if (supportOperator) return chats;
  const canCountUnread = currentUserId != null;
  return Promise.all(chats.map(async (chat) => {
    if (chat.isSupport !== true) return chat;
    let visibleLast = chat.lastMessage && !isTechnicalDiagnosticMessage(chat.lastMessage, true) ? chat.lastMessage : null;
    const roomsWithUnread = canCountUnread && chat.roomUnread?.length ? chat.roomUnread : null;
    const unreadByRoom = roomsWithUnread
      ? new Map<string | null, number>(roomsWithUnread.map((entry) => [entry.roomId ?? null, entry.count] as const))
      : null;
    let unreadRemaining = canCountUnread ? (chat.unreadCount ?? 0) : 0;
    const hiddenByRoom = new Map<string | null, number>();
    let before: number | undefined;
    for (let pageIndex = 0; pageIndex < SUPPORT_DIAGNOSTIC_SCAN_MAX_PAGES && (!visibleLast || (unreadByRoom ? [...unreadByRoom.values()].some((count) => count > 0) : unreadRemaining > 0)); pageIndex++) {
      let page: Page<MessageDto>;
      try {
        page = await loadHistory(chat.id, before, 100);
      } catch {
        break;
      }
      const items = [...page.items].sort((a, b) => b.id - a.id);
      if (items.length === 0) break;
      for (const message of items) {
        const technical = isTechnicalDiagnosticMessage(message, true);
        if (!visibleLast && !technical) visibleLast = message;
        if (!currentUserId || message.senderId === currentUserId) continue;
        const roomId = message.roomId ?? null;
        const remaining = unreadByRoom ? (unreadByRoom.get(roomId) ?? 0) : unreadRemaining;
        if (remaining <= 0) continue;
        if (unreadByRoom) unreadByRoom.set(roomId, remaining - 1);
        else unreadRemaining--;
        if (technical) hiddenByRoom.set(roomId, (hiddenByRoom.get(roomId) ?? 0) + 1);
      }
      if (page.nextBefore == null || page.nextBefore === before) break;
      before = page.nextBefore;
    }
    const correctedRooms = !canCountUnread
      ? chat.roomUnread?.map((entry) => ({ ...entry, count: 0 }))
      : roomsWithUnread?.map((entry) => ({
        ...entry,
        count: Math.max(0, entry.count - (hiddenByRoom.get(entry.roomId ?? null) ?? 0)),
      }));
    const hiddenCount = [...hiddenByRoom.values()].reduce((sum, count) => sum + count, 0);
    return {
      ...chat,
      lastMessage: visibleLast,
      lastMessageAt: visibleLast?.createdAt ?? null,
      unreadCount: !canCountUnread ? 0 : correctedRooms ? correctedRooms.reduce((sum, entry) => sum + entry.count, 0) : Math.max(0, (chat.unreadCount ?? 0) - hiddenCount),
      roomUnread: correctedRooms ?? chat.roomUnread,
    };
  }));
}
